import { useState } from 'react'
import { clsx } from 'clsx'
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import { useRefreshLessonProgress } from '@/hooks/coaching/useRefreshLessonProgress'

interface Props {
  learnerId: number
  lessonId?: number
  /** `workspace` matches the shadcn-based today workspace; `legacy` keeps the existing coaching screens unchanged. */
  appearance?: 'legacy' | 'workspace'
}

export function LessonRefreshButton({ learnerId, lessonId, appearance = 'legacy' }: Props) {
  const { t } = useTranslation('common')
  const refreshLessonProgress = useRefreshLessonProgress(learnerId, lessonId)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await refreshLessonProgress()
    } finally {
      setIsRefreshing(false)
    }
  }

  if (appearance === 'workspace') {
    return <Button type="button" variant="ghost" className="min-h-11 text-muted-foreground" disabled={isRefreshing} onClick={() => void handleRefresh()}>
      <RefreshCw data-icon="inline-start" className={clsx(isRefreshing && 'animate-spin')} />{t('button.refresh')}
    </Button>
  }

  return (
    <button
      type="button"
      onClick={() => void handleRefresh()}
      disabled={isRefreshing}
      aria-label={t('button.refresh')}
      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
    >
      <RefreshCw size={16} className={clsx(isRefreshing && 'animate-spin')} />
      {t('button.refresh')}
    </button>
  )
}
